import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntegrationContext } from '@/contexts/IntegrationContext';
import {
    integrationService,
    type ConnectionCheckResult,
    type IntegrationType,
    type TenantIntegration,
} from './integrationService';

export const INTEGRATIONS_KEY = ['integrations', 'manage'] as const;

/** Every saved integration of the tenant (secrets masked). */
export const useIntegrations = (options?: { enabled?: boolean }) =>
    useQuery<TenantIntegration[]>({
        queryKey: INTEGRATIONS_KEY,
        queryFn: () => integrationService.getIntegrations(),
        enabled: options?.enabled ?? true,
    });

const useRefreshIntegrations = () => {
    const queryClient = useQueryClient();
    const { refreshIntegrations } = useIntegrationContext();
    return () => {
        queryClient.invalidateQueries({ queryKey: INTEGRATIONS_KEY });
        queryClient.invalidateQueries({ queryKey: ['campaigns', 'facebook'] });
        // Click-to-call, mail and message triggers read which channels are on from this context.
        void refreshIntegrations();
    };
};

export interface SaveIntegrationInput {
    /** The saved row when editing, absent when creating. */
    existing?: TenantIntegration;
    type: IntegrationType;
    provider: string;
    /** Masked / untouched secrets must already be left out (see `toConfiguration`). */
    configuration: Record<string, string>;
    /** Desired on/off state; leave undefined to keep it. Enabling goes through `activate`. */
    enabled?: boolean;
}

/**
 * Create or update, then reconcile the on/off state. `POST /manage` activates what it
 * creates, so a provider saved with Enabled off is deactivated right after.
 */
export const useSaveIntegration = () => {
    const refresh = useRefreshIntegrations();

    return useMutation<TenantIntegration, Error, SaveIntegrationInput>({
        mutationFn: async ({ existing, type, provider, configuration, enabled }) => {
            let row = existing
                ? await integrationService.updateIntegration(existing.id, {
                      provider_name: provider,
                      configuration,
                  })
                : await integrationService.createIntegration({
                      integration_type: type,
                      provider_name: provider,
                      configuration,
                  });

            if (enabled !== undefined && enabled !== row.is_active) {
                row = enabled
                    ? await integrationService.activateIntegration(row.id)
                    : await integrationService.deactivateIntegration(row.id);
            }
            return row;
        },
        onSuccess: refresh,
    });
};

/** On/off switch of one saved integration. */
export const useToggleIntegration = () => {
    const refresh = useRefreshIntegrations();

    return useMutation<TenantIntegration, Error, { id: number; enabled: boolean }>({
        mutationFn: ({ id, enabled }) =>
            enabled
                ? integrationService.activateIntegration(id)
                : integrationService.deactivateIntegration(id),
        onSuccess: refresh,
    });
};

export const useDeleteIntegration = () => {
    const refresh = useRefreshIntegrations();

    return useMutation<void, Error, number>({
        mutationFn: id => integrationService.deleteIntegration(id),
        onSuccess: refresh,
    });
};

/** Credentials-only check. Safe: nothing is sent. Persists `last_checked_*` server side. */
export const useCheckConnection = () => {
    const refresh = useRefreshIntegrations();

    return useMutation<ConnectionCheckResult, Error, number>({
        mutationFn: id => integrationService.checkConnection(id),
        onSettled: refresh,
    });
};

/** Real send (SMS, email, call). Persists `last_tested_*` server side. */
export const useSendTest = () => {
    const refresh = useRefreshIntegrations();

    return useMutation<
        { success?: boolean; message?: string },
        Error,
        { id: number; data: Record<string, any> }
    >({
        mutationFn: ({ id, data }) => integrationService.testIntegration(id, data),
        onSettled: refresh,
    });
};
